/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, ActionButton } from '@components/atoms'
import { FlieDropzone } from '@components/molecules'
import Cropper, { ReactCropperElement } from 'react-cropper'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

/** 서버가 아바타를 저장하는 규격과 같은 크기 */
const OUTPUT_SIZE = 512

import 'cropperjs/dist/cropper.css'

interface PropsType {
	onGetImage?: (aFile: File) => void
}

/** 이미지 자르기 — 드롭존 → 크로퍼(헤어라인 프레임) → 삭제(텍스트) · 저장(블랙) */
export const ImageUplodader = (props: PropsType) => {
	const [showOpenFileBrowser, setShowOpenFileBrowser] = React.useState<boolean>(false)
	const [stateFile, setStateFile] = React.useState<File | null>(null)
	const [image, setImage] = React.useState('')

	const cropperRef = React.createRef<ReactCropperElement>()

	const onFileChange = (aFile: File) => {
		setStateFile(aFile)

		const reader = new FileReader()
		reader.addEventListener('load', () => setImage(reader.result?.toString() || ''))

		reader.readAsDataURL(aFile)
	}

	/**
	 * 저장 — **잘라낸 결과**를 넘긴다.
	 *
	 * 예전에는 크롭 화면을 보여주고 `stateFile`(원본)을 그대로 넘겼다(캔버스를 뽑는 줄이
	 * 주석 처리돼 있었다). 그래서 사용자가 배경을 잘라내도 원본이 그대로 올라갔다.
	 * 출력은 512 정사각 PNG — 서버가 아바타를 저장하는 규격과 같다.
	 */
	const getCropData = () => {
		const cropper = cropperRef.current?.cropper

		if (cropper === undefined || stateFile === null) {
			return
		}

		const onGetImage = props.onGetImage

		if (typeof onGetImage !== 'function') {
			return
		}

		const canvas = cropper.getCroppedCanvas({
			width: OUTPUT_SIZE,
			height: OUTPUT_SIZE,
			imageSmoothingQuality: 'high',
		})

		if (canvas === null || canvas === undefined) {
			// 크롭 캔버스를 못 만들면 원본이라도 보낸다 — 저장이 막히는 것보다 낫다
			onGetImage(stateFile)
			return
		}

		canvas.toBlob(blob => {
			if (blob === null) {
				onGetImage(stateFile)
				return
			}

			const baseName: string = stateFile.name.replace(/\.[^.]+$/, '')
			const croppedFile = new File([blob], `${baseName}.png`, { type: 'image/png' })

			onGetImage(croppedFile)
		}, 'image/png')
	}

	const onCloseDropzone = () => {
		setShowOpenFileBrowser(false)
	}

	const onDeleteImage = () => {
		setImage('')
		setStateFile(null)
	}

	return (
		<View css={style}>
			{image ? (
				<View className="imageCropperOuterView">
					<View className="imageCropperView">
						<Cropper
							ref={cropperRef}
							style={{ height: 400, width: '100%' }}
							zoomTo={0.5}
							initialAspectRatio={1}
							aspectRatio={1}
							preview=".img-preview"
							src={image}
							viewMode={1}
							minCropBoxHeight={10}
							minCropBoxWidth={10}
							background={false}
							responsive={true}
							autoCropArea={1}
							checkOrientation={false}
							guides={true}
						/>
					</View>

					<View className="imageCropperToolBarView">
						<ActionButton
							tooltip="이미지 삭제"
							title="이미지 삭제"
							onClick={onDeleteImage}
							iconName="close"
							variant="text"
						/>
						<ActionButton
							tooltip="저장"
							title="저장"
							onClick={getCropData}
							iconName="check"
							buttonColor="primary"
							variant="contained"
						/>
					</View>
				</View>
			) : (
				<FlieDropzone
					onFileChange={onFileChange}
					onFileDropEnd={onCloseDropzone}
					showOpenFileBrowser={showOpenFileBrowser}
					onCloseFileBrowser={onCloseDropzone}
					accept={{
						'image/jpeg': ['.jpeg', '.jpg'],
						'image/png': ['.png'],
						'image/bmp': ['.bmp'],
						'image/gif': ['.gif'],
					}}
				></FlieDropzone>
			)}
		</View>
	)
}

const style = css({
	fontFamily: boFont.ui,
	width: '100%',

	'.imageCropperView': {
		border: `1px solid ${mono.line}`,
		background: mono.surface,
	},

	'.imageCropperToolBarView': {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 8,
		width: '100%',
		marginTop: 12,
	},
})
